import { NormalizedError, ErrorCode } from '../../types/errors';
import { ApiResponse, API_STATUS_CODES } from '../../types/api';
import { HttpError } from '../../services/api/client/httpClient';
import { isRouteErrorResponse } from 'react-router-dom';
import { FetchError } from '../api';

// Map HTTP status codes to error codes
const httpStatusToCode = new Map<number, string>([
  [400, ErrorCode.BAD_REQUEST],
  [401, ErrorCode.UNAUTHORIZED],
  [403, ErrorCode.FORBIDDEN],
  [404, ErrorCode.RESOURCE_NOT_FOUND],
  [422, ErrorCode.VALIDATION],
  [500, ErrorCode.SERVER_ERROR],
  [502, ErrorCode.SERVER_ERROR],
  [503, ErrorCode.SERVER_ERROR],
  [504, ErrorCode.SERVER_ERROR],
]);

// Map API status codes to error codes
const apiStatusToCode = new Map<string, string>([
  [API_STATUS_CODES.FAILURE, ErrorCode.BAD_REQUEST],
  [API_STATUS_CODES.INVALID_ACCESS_TOKEN, ErrorCode.UNAUTHORIZED],
]);

// Base error mapper with common functionality
abstract class BaseErrorMapper {
  protected abstract mapErrorCode(error: unknown): string;
  protected abstract mapErrorMessage(error: unknown): string;
  
  normalize(error: unknown): NormalizedError {
    return {
      code: this.mapErrorCode(error),
      message: this.mapErrorMessage(error),
      details: this.extractDetails(error),
      originalError: error
    };
  }

  protected extractDetails(error: unknown): Record<string, string> | undefined {
    if (!error || typeof error !== 'object') return undefined;
    
    // Handle HttpError type
    if (error instanceof HttpError) {
      const apiResponse = error.response;
      if (apiResponse?.errors?.length) {
        return apiResponse.errors.reduce((acc, err) => ({
          ...acc,
          [err.path]: err.message
        }), {});
      }
    }
    
    return undefined;
  }
}

// Mapper for our enhanced FetchError type
export class FetchErrorMapper extends BaseErrorMapper {
  protected mapErrorCode(error: unknown): string {
    if (!error) return ErrorCode.UNKNOWN;

    // Handle our enhanced FetchError type
    if (error instanceof Error && (error as FetchError).isApiError) {
      const fetchError = error as FetchError;
      
      // If it has HTTP status, map it
      if (fetchError.status) {
        const httpCode = httpStatusToCode.get(fetchError.status);
        if (httpCode) return httpCode;
      }
      
      // If it has API status code, map it
      if (fetchError.apiStatusCode) {
        const apiCode = apiStatusToCode.get(fetchError.apiStatusCode);
        if (apiCode) return apiCode;
      }
      
      // Check for network errors
      if (fetchError.name === 'NetworkError') {
        return ErrorCode.NETWORK_ERROR;
      }
    }

    return ErrorCode.UNKNOWN;
  }

  protected mapErrorMessage(error: unknown): string {
    if (!error) return 'errors.somethingWentWrong';

    // Handle our enhanced FetchError type
    if (error instanceof Error && (error as FetchError).isApiError) {
      const fetchError = error as FetchError;
      
      // If the message is already a translation key, use it
      if (fetchError.message.startsWith('errors.')) {
        return fetchError.message;
      }
      
      // If it has HTTP status, map it to a message
      if (fetchError.status) {
        if (fetchError.status === 404) return 'errors.notFound';
        if (fetchError.status === 403) return 'errors.forbidden';
        if (fetchError.status === 401) return 'errors.unauthorized';
        if (fetchError.status === 400) return 'errors.badRequest';
        if (fetchError.status >= 500) return 'errors.serverError';
      }
      
      // If it has API message, use it
      if (fetchError.apiMessage) {
        return fetchError.apiMessage.startsWith('errors.') 
          ? fetchError.apiMessage 
          : 'errors.somethingWentWrong';
      }
    }

    return 'errors.somethingWentWrong';
  }

  protected extractDetails(error: unknown): Record<string, string> | undefined {
    if (!error || typeof error !== 'object') return undefined;
    
    // Handle our enhanced FetchError type
    if (error instanceof Error && (error as FetchError).isApiError) {
      const fetchError = error as FetchError;
      
      const details: Record<string, string> = {
        name: fetchError.name,
        message: fetchError.message
      };
      
      if (fetchError.url) details.url = fetchError.url;
      if (fetchError.status) details.status = fetchError.status.toString();
      if (fetchError.statusText) details.statusText = fetchError.statusText;
      if (fetchError.apiStatusCode) details.apiStatusCode = fetchError.apiStatusCode;
      if (fetchError.apiMessage) details.apiMessage = fetchError.apiMessage;
      if (fetchError.responseBody) details.responseBody = fetchError.responseBody.substring(0, 200);
      
      return details;
    }
    
    return undefined;
  }
}

// Mapper for our internal API errors
export class InternalApiErrorMapper extends BaseErrorMapper {
  protected mapErrorCode(error: unknown): string {
    if (!error) return ErrorCode.UNKNOWN;

    // Handle HttpError type
    if (error instanceof HttpError) {
      // First try HTTP status code
      const httpCode = httpStatusToCode.get(error.statusCode);
      if (httpCode) return httpCode;

      // Then try API status code
      if (error.response?.statusCode) {
        return apiStatusToCode.get(error.response.statusCode) || ErrorCode.UNKNOWN;
      }
    }

    return ErrorCode.UNKNOWN;
  }

  protected mapErrorMessage(error: unknown): string {
    if (!error) return 'errors.somethingWentWrong';

    // Handle HttpError type
    if (error instanceof HttpError) {
      if (error.statusCode === 404) {
        return 'errors.resourceNotFound';
      }
      return error.message;
    }

    return 'errors.somethingWentWrong';
  }
}

// Mapper for external API errors
export class ExternalApiErrorMapper extends BaseErrorMapper {
  protected mapErrorCode(error: unknown): string {
    if (!error || typeof error !== 'object') return ErrorCode.EXTERNAL_SERVICE_ERROR;

    // Handle HttpError type
    if (error instanceof HttpError) {
      const httpCode = httpStatusToCode.get(error.statusCode);
      if (httpCode) return httpCode;
    }
    
    return ErrorCode.EXTERNAL_SERVICE_ERROR;
  }

  protected mapErrorMessage(error: unknown): string {
    if (!error) return 'errors.somethingWentWrong';

    // Handle HttpError type
    if (error instanceof HttpError) {
      // The error message is already a translation key
      return error.message;
    }

    return 'errors.serverError';
  }
}

// Mapper for React Router errors
export class RouterErrorMapper extends BaseErrorMapper {
  protected mapErrorCode(error: unknown): string {
    if (error && typeof error === 'object' && 'code' in error) {
      return error.code as string;
    }
    if (isRouteErrorResponse(error)) {
      return ErrorCode.ROUTE_NOT_FOUND;
    }
    if (error instanceof Error && error.message === 'errors.pageNotFound') {
      return ErrorCode.ROUTE_NOT_FOUND;
    }
    return ErrorCode.UNKNOWN;
  }

  protected mapErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'data' in error && (error.data as any)?.message) {
      return (error.data as any).message;
    }
    if (isRouteErrorResponse(error)) {
      return 'errors.pageNotFound';
    }
    if (error instanceof Error && error.message === 'errors.pageNotFound') {
      return error.message;
    }
    return 'errors.somethingWentWrong';
  }

  protected extractDetails(error: unknown): Record<string, string> | undefined {
    if (error && typeof error === 'object') {
      const { code, status, data } = error as any;
      return {
        code: code?.toString() || '',
        status: status?.toString() || '',
        ...(data || {})
      };
    }
    
    if (isRouteErrorResponse(error)) {
      return {
        status: error.status?.toString() || '',
        statusText: error.statusText || '',
        ...(error.data || {})
      };
    }
    
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message
      };
    }
    
    return undefined;
  }
}

// Error mapper factory
export class ErrorMapperFactory {
  private static readonly defaultMapper = new InternalApiErrorMapper();
  private static readonly mappers: Record<string, BaseErrorMapper> = {
    internal: ErrorMapperFactory.defaultMapper,
    external: new ExternalApiErrorMapper(),
    router: new RouterErrorMapper(),
    fetch: new FetchErrorMapper()
  };

  static getMapper(source: string = 'internal'): BaseErrorMapper {
    return this.mappers[source] || this.defaultMapper;
  }
}